/** @format */

import React, { useMemo, useState, useCallback } from 'react';
import { Modal, Button, Badge, Spinner } from 'react-bootstrap';
import { BsTelephoneFill } from 'react-icons/bs';
import TelecmiApi from '../../api/TelecmiApi';
import { getTelecmiSoftphoneToken } from '../../utils/telecmiSoftphoneSession';
import './TelecmiLiveCallModal.css';

const TERMINAL_STATUSES = new Set([
  'hangup',
  'ended',
  'failed',
  'busy',
  'noanswer',
]);

function statusVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'answered' || s === 'connected') return 'success';
  if (
    s === 'connecting' ||
    s === 'waiting' ||
    s === 'ringing' ||
    s === 'progress' ||
    s === 'trying' ||
    s === 'started'
  ) {
    return 'warning';
  }
  if (
    s === 'hangup' ||
    s === 'ended' ||
    s === 'failed' ||
    s === 'busy' ||
    s === 'noanswer'
  ) {
    return 'secondary';
  }
  return 'info';
}

function humanCallStatus(status) {
  const s = String(status || '').toLowerCase().trim();
  const map = {
    connecting: 'Connecting…',
    started: 'Call started',
    waiting: 'Waiting…',
    ringing: 'Ringing…',
    progress: 'Connecting…',
    trying: 'Trying…',
    answered: 'Answered',
    connected: 'Connected',
    hangup: 'Call ended',
    ended: 'Call ended',
    failed: 'Call failed',
    busy: 'Line busy',
    noanswer: 'No answer',
    unknown: 'Updating…',
  };
  if (!s) return 'Waiting for status…';
  return map[s] || status;
}

function dialerVisualClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'hangup' || s === 'ended' || s === 'failed' || s === 'busy' || s === 'noanswer') {
    return 'telecmi-dialer-visual--ended';
  }
  if (s === 'answered' || s === 'connected') {
    return 'telecmi-dialer-visual--connected';
  }
  return 'telecmi-dialer-visual--active';
}

function formatDisplayNumber(value) {
  if (value == null || String(value).trim() === '') return '—';
  return String(value).trim();
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

/**
 * Live TeleCMI call progress — dialer-style UI (from | animation | to, status below).
 * Events from Socket.IO `telecmi_call_live`.
 */
export default function TelecmiLiveCallModal({ show, onHide, events }) {
  const [hangupLoading, setHangupLoading] = useState(false);
  const [hangupError, setHangupError] = useState('');

  const rows = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    return [...list].reverse();
  }, [events]);

  const latest = rows[0];
  const fromDisplay = formatDisplayNumber(latest?.from);
  const toDisplay = formatDisplayNumber(latest?.to);
  const statusRaw = latest?.status != null ? String(latest.status) : '';
  const visualClass = dialerVisualClass(statusRaw);

  const cmiuuidTrim =
    latest?.cmiuuid != null && String(latest.cmiuuid).trim() !== ''
      ? String(latest.cmiuuid).trim()
      : '';
  const canHangup =
    !!cmiuuidTrim &&
    !TERMINAL_STATUSES.has(String(statusRaw || '').toLowerCase());
  const softphoneToken = getTelecmiSoftphoneToken();

  const handleHangup = useCallback(async () => {
    setHangupError('');
    const token = getTelecmiSoftphoneToken();
    const id = cmiuuidTrim;
    if (!token || !id) {
      setHangupError(
        !token
          ? 'Sign in to the TeleCMI softphone first (header), then try again.'
          : 'Call ID is not available yet.'
      );
      return;
    }
    setHangupLoading(true);
    try {
      const res = await TelecmiApi.c2cHangup({ token, cmiuuid: id });
      if (res?.data?.success === false) {
        setHangupError(
          res?.data?.message ||
            res?.data?.error ||
            'Hangup request was rejected.'
        );
        return;
      }
    } catch (e) {
      setHangupError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Hangup failed.'
      );
    } finally {
      setHangupLoading(false);
    }
  }, [cmiuuidTrim]);

  return (
    <Modal show={show} onHide={onHide} size="md" centered scrollable>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="h5 mb-0">Call dialer</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <div className="telecmi-dialer">
          <div className="telecmi-dialer-row">
            <div className="telecmi-dialer-endpoint telecmi-dialer-endpoint--from">
              <div className="telecmi-dialer-endpoint-label">From</div>
              <div className="telecmi-dialer-endpoint-num">{fromDisplay}</div>
              {fromDisplay === '—' &&
                latest?.user != null &&
                String(latest.user).trim() !== '' && (
                  <div className="small text-muted mt-1 text-break">
                    TeleCMI user {String(latest.user).trim()}
                  </div>
                )}
            </div>

            <div className={`telecmi-dialer-center telecmi-dialer-visual ${visualClass}`}>
              <div className="telecmi-dialer-rings" aria-hidden>
                <span />
                <span />
                <span />
              </div>
              <div className="telecmi-dialer-icon-wrap">
                <BsTelephoneFill aria-hidden />
              </div>
            </div>

            <div className="telecmi-dialer-endpoint telecmi-dialer-endpoint--to">
              <div className="telecmi-dialer-endpoint-label">To</div>
              <div className="telecmi-dialer-endpoint-num">{toDisplay}</div>
            </div>
          </div>

          <div className="telecmi-dialer-status-block">
            <div className="telecmi-dialer-status-line">
              <Badge bg={statusVariant(statusRaw)}>
                {statusRaw || '—'}
              </Badge>
            </div>
            <p className="telecmi-dialer-status-text">
              {humanCallStatus(statusRaw || 'unknown')}
            </p>
            {latest?.direction != null && String(latest.direction).trim() !== '' && (
              <div className="telecmi-dialer-meta">
                Direction: {String(latest.direction)}
              </div>
            )}
            {latest?.cmiuuid != null && String(latest.cmiuuid).trim() !== '' && (
              <div className="telecmi-dialer-meta font-monospace text-break">
                ID: {String(latest.cmiuuid)}
              </div>
            )}
            {latest?.errorMessage != null && String(latest.errorMessage).trim() !== '' && (
              <div className="telecmi-dialer-meta text-danger small mt-1">
                {String(latest.errorMessage)}
              </div>
            )}
            {hangupError !== '' && (
              <div className="telecmi-dialer-meta text-danger small mt-2">
                {hangupError}
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="telecmi-dialer-activity">
              <div className="telecmi-dialer-activity-title">Activity</div>
              <div className="telecmi-dialer-activity-list">
                {rows.map((ev, idx) => (
                  <div
                    key={`${ev.receivedAt}-${idx}-${ev.cmiuuid || ''}`}
                    className="telecmi-dialer-activity-row"
                  >
                    <Badge bg={statusVariant(ev.status)} className="text-capitalize">
                      {ev.status || '—'}
                    </Badge>
                    <span className="text-muted text-nowrap small">
                      {formatTime(ev.receivedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!latest && (
            <p className="text-muted text-center small mb-0 mt-2">
              Waiting for the first live event from TeleCMI…
            </p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0 d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
          {canHangup && (
            <Button
              variant="danger"
              size="sm"
              disabled={hangupLoading || !softphoneToken}
              onClick={handleHangup}
            >
              {hangupLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Hanging up…
                </>
              ) : (
                'Hang up'
              )}
            </Button>
          )}
          {canHangup && !softphoneToken && (
            <span className="small text-muted ms-2">
              Sign in to TeleCMI softphone to hang up.
            </span>
          )}
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
